import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  ADMIN_TOKEN_STORAGE_KEY,
  apiPostPublic,
} from '../config/api'

type AdminAuthContextValue = {
  token: string | null
  isReady: boolean
  isAdmin: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => void
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null)

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    try {
      const t = localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY)
      setToken(t?.trim() || null)
    } catch {
      setToken(null)
    } finally {
      setIsReady(true)
    }
  }, [])

  useEffect(() => {
    const onSessionLost = () => {
      setToken(null)
    }
    window.addEventListener('proffer:admin-auth-lost', onSessionLost)
    return () => window.removeEventListener('proffer:admin-auth-lost', onSessionLost)
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    const res = await apiPostPublic<{ access_token: string }>('/admin/login', {
      username: username.trim(),
      password,
    })
    const t = res.access_token?.trim()
    if (!t) throw new Error('Άκυρη απάντηση από τον server.')
    localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, t)
    setToken(t)
  }, [])

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY)
    } catch {
      /* ignore */
    }
    setToken(null)
  }, [])

  const value = useMemo(
    () => ({
      token,
      isReady,
      isAdmin: Boolean(token),
      login,
      logout,
    }),
    [token, isReady, login, logout],
  )

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider')
  }
  return ctx
}
